import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, HelpCircle, MessageCircle, MapPin } from 'lucide-react';
import api from '../../api/axios';

const faqs = [
  {
    question: "¿Cómo puedo rastrear mi envío?",
    answer: "Puedes rastrear tu paquete ingresando tu número de guía en la sección de 'Rastreo' de nuestro menú principal. El sistema te mostrará el estado actual y el historial de movimientos en tiempo real."
  },
  {
    question: "¿Cuáles son las sucursales disponibles?",
    answer: "Actualmente contamos con centros operativos estratégicos en Pachuca (Mineral de la Reforma) y Toluca. Puedes consultar la ubicación exacta y el mapa en el menú lateral 'Más'."
  },
  {
    question: "¿Cómo realizo una cotización?",
    answer: "En el menú superior, selecciona 'Cotizar Ahora'. Deberás ingresar las dimensiones, peso y destino de tu paquete para recibir un presupuesto estimado instantáneo."
  },
  {
    question: "¿Qué capacidad de carga tienen sus unidades?",
    answer: "Nuestra flota de unidades NP300 está optimizada para una capacidad máxima de 1,500 kg por viaje, garantizando la seguridad y eficiencia en cada entrega."
  }
];

export default function FAQPage() {
  const [activeIndex, setActiveIndex] = useState(null);
  const [branches, setBranches] = useState([]);
  const [isWADropdownOpen, setIsWADropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await api.get('/branches');
        const raw = res.data?.data || res.data;
        const all = Array.isArray(raw) ? raw : [];
        // Only show branches that have a phone number
        setBranches(all.filter(b => b && b.phone && b.phone.trim() !== ''));
      } catch (err) {
        console.error("Error al cargar sucursales:", err);
        setBranches([]);
      }
    };
    fetchBranches();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsWADropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const openWhatsApp = (branch) => {
    const phone = `52${branch.phone.replace(/\D/g, '')}`;
    const msg = `Hola, vengo de la sección de preguntas frecuentes y tengo una duda. ¿Me pueden ayudar?`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
    setIsWADropdownOpen(false);
  };

  return (
    <div className="min-h-screen py-16 px-6 transition-colors duration-500">
      <div className="max-w-3xl mx-auto space-y-12">
        
        {/* ENCABEZADO */}
        <div className="text-center space-y-4">
          
          {/* ELEMENTO: CENTRO DE AYUDA */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm">
            <HelpCircle size={16} className="text-white" />
            <span className="text-[15px] font-black uppercase tracking-[0.2em] text-white">Centro de Ayuda</span>
          </div>
          
          {/* TÍTULO */}
          <h1 className="text-7xl lg:text-[36px] font-black uppercase tracking-tighter text-white leading-tight">
            Preguntas <span className="text-white underline decoration-[#CE010B] decoration-4 underline-offset-[8px]">Frecuentes</span>
          </h1>
          
          <p className="text-gray-400 font-medium italic text-2xl lg:text-sm px-2">
            Todo lo que necesitas saber sobre tus envíos y logística.
          </p>
        </div>

        {/* ACORDEÓN */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index}
              className={`rounded-[2rem] border-[1.5px] border-black dark:border-white overflow-hidden transition-all duration-500 shadow-lg ${
                activeIndex === index 
                  ? 'bg-[#CE010B] dark:bg-black' 
                  : 'bg-gradient-to-b from-[#F1F1F1] to-[#C1C1C1] dark:from-[#780000] dark:to-[#590000]'
              }`}
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-6 flex items-center justify-between text-left group gap-4"
              >
                <span className={`text-[36px] lg:text-[18px] font-black uppercase tracking-tight transition-colors leading-snug ${
                  activeIndex === index ? 'text-white' : 'text-black dark:text-white'
                }`}>
                  {faq.question}
                </span>
                <div className={`p-2 rounded-full transition-all duration-500 shrink-0
                  ${activeIndex === index 
                    ? 'rotate-180 bg-white text-[#CE010B] dark:text-black' 
                    : 'bg-black/10 text-black dark:bg-white/20 dark:text-white'}`}>
                  <ChevronDown size={20} />
                </div>
              </button>

              <div 
                className={`transition-all duration-500 ease-in-out overflow-hidden
                  ${activeIndex === index ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'}`}
              >
                <div className="p-6 text-[30px] lg:text-[15px] font-medium lg:font-bold leading-relaxed border-t border-white/20 text-white bg-transparent">
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CONTACTO RÁPIDO */}
        <div className="p-8 rounded-[2.5rem] border-[1.5px] border-black dark:border-white flex flex-col md:flex-row items-center justify-between gap-6 transition-all duration-500 bg-white dark:bg-gradient-to-b dark:from-[#780000] dark:to-[#590000]">
          <div className="text-center md:text-left space-y-1">
            <h3 className="text-5xl lg:text-[22px] font-black uppercase tracking-tighter text-black dark:text-white">
              ¿Aún tienes dudas?
            </h3>
            <p className="text-2xl lg:text-[12px] font-medium italic text-gray-500 dark:text-white/70">
              Nuestro equipo de soporte está listo para ayudarte.
            </p>
          </div>

          {/* BOTÓN WHATSAPP CON DROPDOWN */}
          <div className="relative w-full md:w-auto" ref={dropdownRef}>
            <button
              onClick={() => setIsWADropdownOpen(prev => !prev)}
              className="w-full md:w-auto flex items-center justify-center gap-3 px-12 py-5 bg-[#25D366] text-white rounded-2xl font-black uppercase text-2xl lg:text-sm hover:scale-105 transition-all shadow-xl"
            >
              <MessageCircle size={24} />
              WhatsApp
              <ChevronDown
                size={20}
                className={`transition-transform duration-300 ${isWADropdownOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {/* DROPDOWN DE SUCURSALES */}
            {isWADropdownOpen && (
              <div className="absolute bottom-full mb-3 left-0 right-0 md:left-auto md:right-0 md:w-72 bg-white dark:bg-neutral-900 border border-black/10 dark:border-white/10 rounded-2xl shadow-2xl overflow-hidden z-50 animate-in zoom-in-95 duration-200">
                <p className="px-4 py-3 text-[11px] lg:text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-white/40 border-b border-black/5 dark:border-white/5">
                  Selecciona una sucursal
                </p>
                {branches.length === 0 ? (
                  <p className="px-4 py-4 text-sm text-gray-400 text-center italic">
                    Sin sucursales disponibles
                  </p>
                ) : (
                  branches.map((branch) => (
                    <button
                      key={branch.id}
                      onClick={() => openWhatsApp(branch)}
                      className="w-full flex items-center gap-3 px-4 py-4 lg:py-3 hover:bg-[#25D366]/10 dark:hover:bg-[#25D366]/20 transition-colors text-left group"
                    >
                      <div className="w-9 h-9 lg:w-8 lg:h-8 rounded-full bg-[#25D366]/10 flex items-center justify-center shrink-0 group-hover:bg-[#25D366]/20 transition-colors">
                        <MapPin size={16} className="text-[#25D366]" />
                      </div>
                      <div className="flex flex-col leading-tight">
                        <span className="text-xl lg:text-sm font-black text-black dark:text-white uppercase tracking-tight">
                          {branch.name}
                        </span>
                        <span className="text-base lg:text-[11px] text-gray-400 dark:text-white/40 font-medium">
                          {branch.phone}
                        </span>
                      </div>
                      <MessageCircle size={16} className="ml-auto text-[#25D366] opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}